interface ApiErrorItem {
  field?: string;
  message?: string;
}

interface ParsedErrors {
  fieldErrors: Record<string, string>;
  generalMessage: string;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/** Рекурсивно извлекает массив ошибок из типичных обёрток API. */
function unwrapErrorPayload(errorData: unknown): unknown {
  if (!isRecord(errorData)) {
    return errorData;
  }
  const errors = errorData.errors;
  if (Array.isArray(errors)) {
    return errors;
  }
  const violations = errorData.violations;
  if (Array.isArray(violations)) {
    return violations;
  }
  const details = errorData.detail;
  if (Array.isArray(details)) {
    return details;
  }
  return errorData;
}

export function parseApiError(errorData: unknown): ParsedErrors {
  const unwrapped = unwrapErrorPayload(errorData);
  const fieldErrors: Record<string, string> = {};
  let generalMessage = 'Произошла ошибка';

  // Массив ошибок вида [{ field, message }, ...] (в т.ч. после unwrap)
  if (Array.isArray(unwrapped)) {
    const lines: string[] = [];
    unwrapped.forEach((error: ApiErrorItem) => {
      if (!error || typeof error !== 'object') return;
      const msg = typeof error.message === 'string' ? error.message.trim() : '';
      if (!msg) return;
      if (error.field) {
        fieldErrors[error.field] = msg;
      }
      lines.push(msg);
    });

    if (lines.length > 0) {
      generalMessage = lines.join('. ');
    } else if (Object.keys(fieldErrors).length > 0) {
      generalMessage = Object.values(fieldErrors).join('. ');
    }
  }
  // Объект с message (без массива ошибок на верхнем уровне после unwrap)
  else if (
    isRecord(unwrapped) &&
    'message' in unwrapped &&
    typeof unwrapped.message === 'string' &&
    unwrapped.message.trim()
  ) {
    generalMessage = unwrapped.message.trim();
  }
  // RFC 7807 / Problem Details: detail — строка
  else if (isRecord(unwrapped) && typeof unwrapped.detail === 'string' && unwrapped.detail.trim()) {
    generalMessage = unwrapped.detail.trim();
  }
  // Строка целиком
  else if (typeof unwrapped === 'string' && unwrapped.trim()) {
    generalMessage = unwrapped.trim();
  }

  return {
    fieldErrors,
    generalMessage,
  };
}

export function safeParseApiError(error: unknown): ParsedErrors {
  const errorData =
    error && typeof error === 'object' && 'data' in error
      ? (error as { data: unknown }).data
      : error;

  return parseApiError(errorData);
}

/** Текст для тостов; безопасно для unknown (нет обращения к .message у не-Error). */
export function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'string' && error.trim()) return error.trim();
  return fallback;
}
